import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Clock,
  CheckCircle,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  FileText,
  LogIn,
  Loader2,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-blue-100 text-blue-800", icon: Clock },
  contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-800", icon: MessageSquare },
  quoted: { label: "Quoted", color: "bg-purple-100 text-purple-800", icon: FileText },
  booked: { label: "Booked", color: "bg-green-100 text-green-800", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800", icon: CheckCircle },
};

const serviceLabels = {
  doors: "Door Fitting",
  trimwork: "Skirting & Architraves",
  panelling: "Wall Panelling",
  sanding: "Floor Sanding",
  multiple: "Multiple Services",
  carpentry: "Carpentry",
  both: "Multiple Services",
};

const MembersPage = () => {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchEnquiries();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/members/enquiries`, {
        withCredentials: true,
      });
      setEnquiries(response.data);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Not logged in state
  if (!authLoading && !user) {
    return (
      <div>
        <Helmet>
          <title>My Enquiries | Expert Carpenter</title>
        </Helmet>

        <section className="py-16 sm:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              My Enquiries
            </h1>
            <p className="text-lg text-gray-300">
              Sign in to view and track your quote requests
            </p>
          </div>
        </section>

        <section className="py-16 bg-[#f8f7f5]">
          <div className="max-w-md mx-auto px-4">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#2D5A4A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <LogIn className="h-8 w-8 text-[#2D5A4A]" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Sign in to continue
                </h2>
                <p className="text-gray-600 mb-6">
                  Sign in with Google to view your enquiries and track the status of your quotes.
                </p>
                <Button
                  onClick={login}
                  className="w-full bg-[#2D5A4A] hover:bg-[#234539] text-white h-12 text-base"
                  data-testid="members-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f7f5]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#2D5A4A] mx-auto mb-4" />
          <p className="text-gray-600">Loading your enquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>My Enquiries | Expert Carpenter</title>
      </Helmet>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-14 h-14 rounded-full border-3 border-[#C9A227]"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#C9A227] flex items-center justify-center text-black font-bold text-xl">
                {user.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Welcome, {user.name?.split(" ")[0]}
              </h1>
              <p className="text-gray-400">{user.email}</p>
            </div>
          </div>
          <p className="text-gray-300">
            Track the status of your quote requests below
          </p>
        </div>
      </section>

      {/* Enquiries Section */}
      <section className="py-10 sm:py-14 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4">
          {enquiries.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  No enquiries yet
                </h2>
                <p className="text-gray-600 mb-6">
                  You haven't submitted any quote requests yet. Get started by requesting a free quote.
                </p>
                <Button
                  onClick={() => navigate("/contact")}
                  className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold"
                  data-testid="get-quote-btn"
                >
                  Get a Free Quote
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Your Enquiries ({enquiries.length})
                </h2>
                <Button
                  onClick={() => navigate("/contact")}
                  className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold"
                  size="sm"
                >
                  New Quote Request
                </Button>
              </div>

              {enquiries.map((enquiry) => {
                const status = statusConfig[enquiry.status] || statusConfig.new;
                const StatusIcon = status.icon;

                return (
                  <Card
                    key={enquiry.id}
                    className="border-0 shadow-md hover:shadow-lg transition-shadow"
                    data-testid={`enquiry-card-${enquiry.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className={`${status.color} flex items-center gap-1`}>
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {formatDate(enquiry.timestamp)}
                            </span>
                          </div>

                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {serviceLabels[enquiry.service_type] || enquiry.service_type}
                          </h3>

                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {enquiry.message}
                          </p>

                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Phone className="h-4 w-4" />
                              {enquiry.phone}
                            </span>
                            {enquiry.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-4 w-4" />
                                {enquiry.email}
                              </span>
                            )}
                            {enquiry.postcode && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {enquiry.postcode}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="sm:text-right">
                          <p className="text-xs text-gray-400 uppercase tracking-wide">
                            Reference
                          </p>
                          <p className="font-mono text-sm text-gray-600">
                            {enquiry.id.slice(0, 8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MembersPage;
